<template>
  <v-card
    ref="card"
    class="base-screen-card"
    :class="{
      hide: readyToHide,
      'has-height': !!height,
      'animate-open': animateOpen && !animatedOpen,
    }"
    :style="cssVars"
    @transitionend="onTransitionEnd"
    @animationend="onAnimationEnd"
  >
    <!-- Main content (title, metadata, abstract/pdf) -->
    <div class="main-content px-4 py-3">
      <!-- Title -->
      <highlightable-content
        class="pub-title"
        :highlight-terms="highlights"
      >
        {{ publication.title }}
      </highlightable-content>

      <!-- Metadata (author, year, publisher) -->
      <div class="pub-metadata mb-2">
        <div
          v-if="reference.studyNumber"
          class="study-id"
          data-testid="study-id"
        >
          {{ reference.displayStudyId }}
        </div>
        <div class="author-and-year">
          {{ publication.firstAuthor() }}
          {{ publication.year || '' }}
        </div>
        <div class="place-published">
          {{ publication.placePublished }}
        </div>
      </div>

      <!-- Status chips (comments, tags, pdf) -->
      <div
        v-if="showStatusChips"
        class="status-chips d-flex align-center mb-2"
      >
        <icon-badge
          v-if="reference.comments.length"
          icon="mode_comment"
          :label="reference.comments.length"
          icon-bg-class="transparent"
          label-bg-class="transparent"
        />
        <icon-badge
          v-if="reference.tags.length"
          icon="local_offer"
          :label="reference.tags.length"
          icon-bg-class="transparent"
          label-bg-class="transparent"
        />
        <icon-badge
          v-if="reference.hasFullTextPdf()"
          icon="picture_as_pdf"
          label="PDF"
          icon-bg-class="transparent"
          label-bg-class="transparent"
        />
      </div>

      <!-- Large content (abstract or full text) -->
      <template v-if="!hideLargeContent">
        <expandable-full-text-panel
          v-if="showFullText"
          :reference="reference"
        />
        <expandable-abstract
          v-else-if="publication.abstract"
          :abstract="publication.abstract"
          :highlights="highlights"
        />
        <v-chip v-else color="primary" small @click="onEdit">
          <v-icon left small>add</v-icon> Add Abstract
        </v-chip>
      </template>
    </div>
    <!-- Slot for stage-specific detail sections (e.g. full-text review details) -->
    <slot name="details"></slot>
    <!-- Actions section -->
    <div class="actions px-4 py-4">
      <!-- Open button -->
      <action-button color="grey lighten-3" @click="$emit('open')">
        <v-icon left color="grey darken-1">zoom_out_map</v-icon>
        <span class="grey--text text--darken-1">Open</span>
      </action-button>

      <!-- Slot to add stage specific actions -->
      <slot name="actions">Actions Slot</slot>
    </div>
  </v-card>
</template>

<script>
import ActionButton from '@/components/Common/ActionButton';
import IconBadge from '@/components/Common/IconBadge';
import ExpandableFullTextPanel from '@/components/FullTextReviewPageV2/ReviewTab/ExpandableFullTextPanel';
import HighlightableContent from '@/components/Common/HighlightableContent';
import ExpandableAbstract from './ExpandableAbstract';
import { Reference } from '@/models';
import { mapActions } from 'vuex';

export default {
  name: 'BaseScreenCard',
  components: {
    ActionButton,
    IconBadge,
    ExpandableFullTextPanel,
    ExpandableAbstract,
    HighlightableContent,
  },
  props: {
    reference: {
      type: Reference,
      required: true,
    },
    showFullText: {
      type: Boolean,
      default: false,
    },
    hideLargeContent: {
      type: Boolean,
      default: false,
    },
    hide: {
      type: Boolean,
      default: false,
    },
    animateOpen: {
      type: Boolean,
      default: false,
    },
    highlights: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'open',
    'save-reference',
    'hidden',
    'animated-open',
    'edit-field',
  ],
  data: () => ({
    height: null,
    readyToHide: false,
    hideTriggerTimeout: null,
    openTriggerTimeout: null,
    animatedOpen: false,
  }),
  computed: {
    publication() {
      return this.reference.publication;
    },
    showStatusChips() {
      return (
        this.reference.comments.length ||
        this.reference.tags.length ||
        this.reference.hasFullTextPdf()
      );
    },
    cssVars() {
      return {
        '--base-screen-card-height': `${this.height}px`,
      };
    },
  },
  watch: {
    hide(hide) {
      if (hide) {
        this.triggerHideAnimation();
      }
    },
  },
  mounted() {
    if (this.animateOpen) {
      this.openTriggerTimeout = setTimeout(() => {
        this.height = this.$refs.card.$el.scrollHeight;
      }, 150);
    }
  },
  destroyed() {
    clearTimeout(this.hideTriggerTimeout);
    clearTimeout(this.openTriggerTimeout);
  },
  methods: {
    ...mapActions({
      saveReference: 'reference/updateReferenceFields',
    }),
    onEdit() {
      this.$emit('edit-field', 'abstract');
    },
    triggerHideAnimation() {
      this.height = this.$refs.card.$el.scrollHeight;
      this.hideTriggerTimeout = setTimeout(() => {
        this.readyToHide = true;
      }, 150);
    },
    onTransitionEnd(e) {
      if (this.hide && e.propertyName === 'height') {
        this.$emit('hidden');
      }
    },
    onAnimationEnd(e) {
      if (e.animationName.includes('animate-open')) {
        this.animatedOpen = true;
        this.height = null;
        this.$emit('animated-open');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/styles.sass';

.base-screen-card {
  .main-content {
    border-bottom: 1px solid map-get($grey, 'lighten-2');
  }

  .pub-title {
    font-weight: 400;
  }

  .pub-metadata {
    display: flex;
    gap: 12px;

    .study-id {
      color: map-get($grey, 'lighten-1');
    }
    .author-and-year {
      color: map-get($grey, 'base');
    }
    .place-published {
      color: map-get($grey, 'lighten-1');
    }
  }

  .actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: map-get($grey, 'lighten-5');
  }

  &.has-height {
    overflow: hidden;
    transition: height 0.5s ease-in-out,
      margin-bottom 0.5s ease-in-out;
    height: var(--base-screen-card-height);
  }

  &.hide {
    height: 0px;
    margin-bottom: 0px;
  }

  &.animate-open {
    animation: animate-open 0.5s ease-in-out;
    animation-fill-mode: forwards;
    overflow: hidden;
  }

  @keyframes animate-open {
    0% {
      height: 0px;
      margin-bottom: 0px;
    }
    100% {
      height: var(--base-screen-card-height);
    }
  }
}
</style>
